
import 'driver.js/dist/driver.min.css'
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator'
import Modal from '@/components/Modal.vue'
import { sendMessage, sendMoment } from '@/api/notices'
import { isEmptyString } from '@/utils/string'
import {
    convertNoticeComposerToMomentRequest,
    noticeTypeTitle,
} from '@/utils/notices'
import { Mutation, namespace } from 's-vuex-class'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { IMessage, IRecipient } from '@/entities/notices'
import NoticeRow from '@/components/Notices/NoticeRow.vue'
import NoticeMessage from '@/components/Notices/NoticeMessage.vue'
import NoticeSchedulerModal from '@/components/Notices/NoticeSchedulerModal.vue'
import RecipientPicker from '@/components/Notices/RecipientPicker.vue'
import NoticeTypePicker from '@/components/Notices/NoticeTypePicker.vue'
import Quote from '@/components/Notices/Quote.vue'
import DesktopRecipientPicker from '@/components/Notices/DesktopRecipientPicker.vue'
import RecipientPickerModal from '@/components/Notices/RecipientPickerModal.vue'
import SelectedRecipientPills from '@/components/Notices/SelectedRecipientPills.vue'
import NoticesMixin from '@/components/Notices/notices-mixin'
import ChatInput from '@/components/Chat/ChatInput.vue'
import Typography from '@/components/Rain/Typography/Typography.vue'
import BaseButton from '@/components/Rain/Buttons/BaseButton.vue'
import { UserDetails } from '@/mobile/src/types/auth'
import { MessageTypeLabel, ModalSource } from '@/mobile/src/types/messageTypes'
import { Getter } from 's-vuex-class'

const AuthModule = namespace('auth')

@Component<NoticesComposer>({
    components: {
        SelectedRecipientPills,
        RecipientPickerModal,
        BaseButton,
        Typography,
        DesktopRecipientPicker,
        Quote,
        ChatInput,
        Modal,
        NoticeMessage,
        NoticeRow,
        NoticeSchedulerModal,
        NoticeTypePicker,
        RecipientPicker,
        FontAwesomeIcon,
    },
})
export default class NoticesComposer extends Mixins(NoticesMixin) {
    @Prop(Boolean) public show!: boolean
    @Prop({ type: String, default: '' }) public sendButtonTextOverride!: string
    @Prop({ type: Boolean, default: true }) public canPickRecipients!: boolean
    @Prop({ type: Boolean, default: true }) public canPickSchedule!: boolean
    @Prop({ type: Boolean, default: false }) public resetOnShow!: boolean
    @Prop({ type: String, default: 'type' }) public startStep!: string
    @Prop({ type: Object, default: () => ({}) }) public notice!: {
        type: string
        message: string
        recipients: IRecipient[]
        question: null | object
    }
    @Prop(Function) public saveOverride!: (IComposer) => any

    @AuthModule.Getter private details!: UserDetails

    @Getter $companyVars

    @Mutation private setMessage
    private saving = false
    private composingStep = '' // ['message', 'type', 'recipient']
    private modalHeight = 480
    private maxModalWidth = 600

    @Watch('show')
    onShowChange(show) {
        if (this.resetOnShow) {
            if (show) {
                //Reset recipients & question on show
                this.selectedRecipients = [...this.composer.recipients]

                if (this.notice?.question) {
                    this.setComposerQuestion(this.notice.question)
                }
            } else {
                this.showNoticeTypePicker()
            }
        }
    }

    public beforeMount() {
        this.composingStep = this.startStep
        if (this.notice) {
            // set notice type
            this.setComposerNoticeType(this.notice.type)
            this.setComposerMessage(this.notice.message)
            this.setComposerRecipients(this.notice.recipients ?? [])
            if (this.notice?.question) {
                this.setComposerQuestion(this.notice.question)
            }
        }
    }

    public async mounted() {
        this.selectedRecipients = [...this.composer.recipients]
        if (!this.timezone) {
            this.loadTimezone()
        }
    }

    private onSelectNoticeType(noticeType) {
        this.setComposerNoticeType(noticeType)
        this.composingStep = 'message'
    }

    private showRecipientPicker() {
        this.composingStep = 'recipient'
        if (!this.recipients || !this.recipients.length) {
            this.loadRecipients()
        }
    }

    private showNoticeTypePicker() {
        this.composingStep = this.startStep
    }

    private async sendNoticeMessage() {
        if (!this.canSend) {
            return
        }
        if (this.saveOverride) {
            this.saving = true
            const success = await this.saveOverride(this.composer)
            this.saving = false
            if (success) {
                // reset when success
                this.composingStep = this.startStep
                this.scheduledTimeLabel = 'Now'
                this.selectedRecipients = []
                this.resetComposer()
            }
            return
        }

        // Notices without scheduled_at will trigger moment creation.
        if (!this.composer.scheduled_at) {
            await this.sendMomentNoticeMessage()
            return
        }

        this.saving = true
        const { data } = await sendMessage(this.composer)
        this.saving = false
        this.setErrorMessage(data.msg)
        if (data.success) {
            data.scheduled_at = this.composer.scheduled_at
            this.composingStep = 'type' //reset display
            this.scheduledTimeLabel = 'Now'
            this.selectedRecipients = []

            this.notifySuccess(data)
            this.resetComposer()
            this.$emit('notice-sent', data)
        } else {
            this.notifyError(data)
        }
    }

    private async sendMomentNoticeMessage() {
        this.saving = true
        const { data } = await sendMoment(
            convertNoticeComposerToMomentRequest(this.composer)
        )
        this.saving = false

        if (data.success) {
            data.msg = 'Notice sent' // Replace moment create message with notice sent.
            this.composingStep = 'type' //reset display
            this.scheduledTimeLabel = 'Now'
            this.selectedRecipients = []
            this.notifySuccess(data)
            this.resetComposer()
            this.$emit('notice-sent', data)
        } else {
            this.setErrorMessage(data.error)
            this.notifyError(data)
        }
    }

    private get editingMessage() {
        return this.composer.message
    }

    private get canSend(): boolean {
        return !!(
            this.editingMessage &&
            !isEmptyString(this.editingMessage) &&
            this.contentLength <= this.contentLengthLimit &&
            (!this.canPickRecipients ||
                (this.canPickRecipients && this.hasRecipients)) &&
            !this.saving
        )
    }

    private get typeTitle() {
        return noticeTypeTitle(this.composer.type)
    }

    private async onClose() {
        this.$emit('close')
    }

    private onCancelRecipients() {
        this.composingStep = 'message'
        this.selectedRecipients = this.composer.recipients
    }

    private onConfirmRecipients(confirmedRecipients: IRecipient[]) {
        this.composingStep = 'message'
        this.setComposerRecipients(confirmedRecipients)
        this.selectedRecipients = confirmedRecipients
    }
}
